/**
 * Prompt Templates API Service
 */
import IService from '../IService';

export default IService.extend({
  /**
   * Get all templates accessible to the current user
   * @param {string} contextScope - Optional scope filter: USER | ORGANIZATION | PUBLIC
   * @returns {Promise<Array>} Array of template objects
   */
  getTemplates(contextScope = null) {
    return new Promise((resolve, reject) => {
      const url = contextScope
        ? app.apiUrl(`/api/v1/promptTemplates?contextScope=${contextScope}`)
        : app.apiUrl('/api/v1/promptTemplates');

      $.ajax({
        url: url,
        type: 'GET',
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        success: (response) => {
          // Sort by lastModifiedDate descending
          const templates = Array.isArray(response) ? response : [];
          templates.sort((a, b) => {
            const dateA = new Date(a.lastModifiedDate).getTime();
            const dateB = new Date(b.lastModifiedDate).getTime();
            return dateB - dateA;
          });
          resolve(templates);
        },
      });
    });
  },

  /**
   * Get a single template by ID
   * @param {string} id - Template ID (UUID)
   * @returns {Promise<Object>} Template object
   */
  getTemplate(id) {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: app.apiUrl(`/api/v1/promptTemplates/${id}`),
        type: 'GET',
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        success: (response) => {
          resolve(response);
        },
      });
    });
  },

  /**
   * Create a new template
   * @param {Object} template - Template object { name, promptText, description?, scope }
   * @returns {Promise<Object>} Created template
   */
  createTemplate(template) {
    return new Promise((resolve, reject) => {
      if (!template.name || !template.name.trim()) {
        reject(
          this.createError({
            status: 400,
            responseText: 'Template name is required',
          })
        );
        return;
      }
      if (!template.promptText || !template.promptText.trim()) {
        reject(
          this.createError({
            status: 400,
            responseText: 'Template content is required',
          })
        );
        return;
      }

      const requestData = {
        name: template.name.trim(),
        promptText: template.promptText.trim(),
        description: template.description || '',
        scope: template.scope || 'ORGANIZATION',
      };

      $.ajax({
        url: app.apiUrl('/api/v1/promptTemplates'),
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify(requestData),
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        success: (response) => {
          resolve(response);
        },
      });
    });
  },

  /**
   * Update an existing template
   * @param {string} id - Template ID (UUID)
   * @param {Object} updates - Updates to apply { name?, promptText?, description?, scope? }
   * @returns {Promise<Object>} Updated template
   */
  updateTemplate(id, updates) {
    return new Promise((resolve, reject) => {
      const requestData = {};

      if (updates.name !== undefined) {
        requestData.name = updates.name.trim();
      }
      if (updates.promptText !== undefined) {
        requestData.promptText = updates.promptText.trim();
      }
      if (updates.description !== undefined) {
        requestData.description = updates.description;
      }
      if (updates.scope !== undefined) {
        requestData.scope = updates.scope;
      }

      $.ajax({
        url: app.apiUrl(`/api/v1/promptTemplates/${id}`),
        type: 'PUT',
        contentType: 'application/json',
        data: JSON.stringify(requestData),
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        success: (response) => {
          resolve(response);
        },
      });
    });
  },

  /**
   * Save a template (create new or update existing)
   * @param {Object} template - Template object { id?, name, promptText, description?, scope? }
   * @returns {Promise<Object>} Saved template
   */
  saveTemplate(template) {
    if (template.id) {
      // Update existing template
      return this.updateTemplate(template.id, {
        name: template.name,
        promptText: template.promptText,
        description: template.description,
        scope: template.scope,
      });
    } else {
      // Create new template
      return this.createTemplate(template);
    }
  },

  /**
   * Delete a template
   * @param {string} id - Template ID (UUID)
   * @returns {Promise<boolean>} Success status
   */
  deleteTemplate(id) {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: app.apiUrl(`/api/v1/promptTemplates/${id}`),
        type: 'DELETE',
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        success: () => {
          resolve(true);
        },
      });
    });
  },

  /**
   * Check if a template name already exists
   * @param {string} name - Template name to check
   * @param {string} excludeId - Optional ID to exclude from check (for updates)
   * @returns {Promise<Object|null>} Existing template or null
   */
  findTemplateByName(name, excludeId = null) {
    return this.getTemplates().then((templates) => {
      const normalizedName = name.trim().toLowerCase();
      const existing = templates.find(
        (t) =>
          t.name.toLowerCase() === normalizedName &&
          t.id !== excludeId &&
          t.scope !== 'PUBLIC'
      );
      return existing || null;
    });
  },
});
